'use client'

import { useDraggable } from '@dnd-kit/core'
import { addDays, addHours, differenceInMinutes } from 'date-fns'
import { atom, useAtom } from 'jotai'
import type {
  ComponentProps,
  CSSProperties,
  FC,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  RefObject,
} from 'react'
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import { buildGanttDaySegments, buildGanttMonthSegments } from './header-layout'
import { cn } from '@/lib/utils'
import { getOffset, getWidth } from './gantt-offset'
import { durationAsSecondsToString, format } from '@/utils'
import { PencilIcon } from 'lucide-react'
import { isMobile } from 'react-device-detect'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from './context-menu'
import { Card } from './card'
import { useTranslate } from '@ur-solutions/react-hooks'

const draggingAtom = atom(false)
const scrollXAtom = atom(0)

export const useGanttDragging = () => useAtom(draggingAtom)
export const useGanttScrollX = () => useAtom(scrollXAtom)

export type GanttStatus = {
  id: string
  name: string
  color: string
  active?: boolean
}

export type GanttFeature = {
  id: string | null
  name: string | null
  startAt: Date
  endAt: Date
  duration?: number
  hideDuration?: boolean
  status?: GanttStatus
  group?: GanttStatus
  tooltipContent?: React.ReactNode
  manuallyEntered?: boolean
  labelClassName?: string
}

export type GanttMarkerProps = {
  id: string
  date: Date
  label: string
}

export type Range = 'daily'

export type TimelineData = Date[]

export type GanttContextProps = {
  zoom: number
  range: Range
  columnWidth: number
  columnDurationMinutes: number
  sidebarWidth: number
  headerHeight: number
  datetimeStart: Date
  datetimeEnd: Date
  rowHeight: number
  placeholderLength: number
  timelineData: TimelineData
  ref: RefObject<HTMLDivElement | null> | null
  scrollToFeature?: (feature: GanttFeature) => void
  /**
   * When true, every Card-backed tooltip inside this Gantt opts into the
   * cursor-following tooltip variant. Set by `GanttProvider`'s
   * `cursorFollowTooltips` prop. Default false preserves the anchored
   * Radix Tooltip used by salary/timesheets Gantts.
   */
  cursorFollowTooltips: boolean
}
const createInitialTimelineData = (datetimeStart: Date, datetimeEnd: Date) => {
  const data: TimelineData = []

  let currentDate = datetimeStart
  while (currentDate <= datetimeEnd) {
    data.push(currentDate)
    currentDate = addDays(currentDate, 1)
  }

  return data
}

// getOffset / getWidth live in ./gantt-offset (pure, unit-tested). They use
// fractional-minute math so feature bars stay additive and don't render phantom
// gaps when activities carry second-precision timestamps.

const GanttContext = createContext<GanttContextProps>({
  zoom: 100,
  range: 'daily',
  columnWidth: 50,
  columnDurationMinutes: 60,
  headerHeight: 60,
  sidebarWidth: 300,
  rowHeight: 36,
  placeholderLength: 2,
  timelineData: [],
  ref: null,
  scrollToFeature: undefined,
  datetimeStart: new Date(),
  datetimeEnd: addDays(new Date(), 1),
  cursorFollowTooltips: false,
})

export type GanttContentHeaderProps = {
  renderHeaderItem: (index: number) => ReactNode
  title: string
  columns: number
}

export const GanttContentHeader: FC<GanttContentHeaderProps> = ({
  title,
  columns,
  renderHeaderItem,
}) => {
  const id = useId()

  return (
    <div
      className="sticky top-0 z-20 grid shrink-0 bg-backdrop/90"
      style={{ height: 'var(--gantt-header-height)' }}
    >
      <div>
        <div
          className="sticky inline-flex whitespace-nowrap px-3 py-2 text-muted-foreground text-xs"
          style={{
            left: `calc(var(--gantt-sidebar-width))`,
            width: '70px',
          }}
        >
          <p>{title}</p>
        </div>
      </div>
      <div
        className="grid w-full"
        style={{
          gridTemplateColumns: `repeat(${columns}, var(--gantt-column-width))`,
        }}
      >
        {Array.from({ length: columns }).map((_, index) => (
          <div
            className="shrink-0 border-border/50 border-b py-1 text-center text-xs"
            key={`${id}-${index}`}
          >
            {renderHeaderItem(index)}
          </div>
        ))}
      </div>
    </div>
  )
}

const DailyHeader: FC = () => {
  const gantt = useContext(GanttContext)

  // Column width in px per hour (1 hour = 1 column). At low zoom the labels
  // collide ("20:0021:0022:00..."). Skip every Nth label by blanking its
  // text so the column count (= grid alignment) is preserved.
  const pxPerHour = (gantt.columnWidth * gantt.zoom) / 2400
  const labelStepHours =
    pxPerHour >= 50
      ? 1
      : pxPerHour >= 25
      ? 2
      : pxPerHour >= 16
      ? 3
      : pxPerHour >= 8
      ? 6
      : pxPerHour >= 3
      ? 12
      : 24 // ultra zoom-out: only midnight gets a label

  const showHourTicks = pxPerHour >= 5

  const dayLabelMinPx = 70
  const daySegments = useMemo(() => {
    return buildGanttDaySegments(gantt.datetimeStart, gantt.datetimeEnd)
  }, [gantt.datetimeEnd, gantt.datetimeStart])
  const monthSegments = useMemo(() => {
    return buildGanttMonthSegments(daySegments)
  }, [daySegments])
  const minDayWidthPx = daySegments.length
    ? Math.min(...daySegments.map(segment => segment.hours.length * pxPerHour))
    : 24 * pxPerHour
  const dayLabelEveryN =
    minDayWidthPx >= dayLabelMinPx
      ? 1
      : Math.ceil(dayLabelMinPx / Math.max(minDayWidthPx, 1))
  const useShortDayLabel = minDayWidthPx < dayLabelMinPx * 1.6
  const useMonthHeaders = minDayWidthPx < dayLabelMinPx

  const totalColumns = daySegments.reduce(
    (sum, segment) => sum + segment.hours.length,
    0
  )
  return (
    <div className="relative flex flex-col">
      <div className="relative flex flex-row">
        {useMonthHeaders
          ? monthSegments.map(segment => (
              <GanttContentHeader
                key={segment.id}
                columns={segment.columns}
                renderHeaderItem={() => <div />}
                title={format(segment.date, 'MMM yyyy')}
              />
            ))
          : daySegments.map((segment, dayIndex) => {
              const firstHour = segment.hours[0]
              const showDayLabel = dayIndex % dayLabelEveryN === 0
              const titleStr =
                !showDayLabel || !firstHour
                  ? ''
                  : useShortDayLabel
                  ? format(firstHour, 'dd.MM.yy')
                  : segment.id

              return (
                <GanttContentHeader
                  key={segment.id}
                  columns={Math.max(segment.hours.length, 0)}
                  renderHeaderItem={index => {
                    if (!showHourTicks) return <div />
                    const hour = segment.hours[index + 1]
                    if (hour === undefined) {
                      const backupHour = segment.hours[segment.hours.length - 1]
                      const last = addHours(backupHour ?? new Date(), 1)
                      const showLabel = last.getHours() % labelStepHours === 0
                      return (
                        <div
                          className="flex items-center justify-center gap-1"
                          style={{
                            marginLeft: 'calc(var(--gantt-column-width))',
                            zIndex: 20000,
                          }}
                        >
                          <p className="text-muted-foreground">
                            {showLabel ? format(last, 'HH:mm') : ''}
                          </p>
                        </div>
                      )
                    }
                    const showLabel = hour.getHours() % labelStepHours === 0
                    return (
                      <div
                        className="flex items-center justify-center gap-1"
                        style={{
                          marginLeft: 'calc(var(--gantt-column-width))',
                          zIndex: 2000,
                        }}
                      >
                        <p className="text-muted-foreground">
                          {showLabel ? format(hour, 'HH:mm') : ''}
                        </p>
                      </div>
                    )
                  }}
                  title={titleStr}
                />
              )
            })}
      </div>
      {pxPerHour >= 5 ? (
        <GanttColumns columns={totalColumns} />
      ) : (
        <GanttColumns
          columnUnits={
            useMonthHeaders
              ? monthSegments.map(segment => segment.columns)
              : daySegments.map(segment => segment.hours.length)
          }
        />
      )}
    </div>
  )
}

export type GanttHeaderProps = {
  className?: string
}

export const GanttHeader: FC<GanttHeaderProps> = ({ className }) => {
  return (
    <div
      className={cn(
        '-space-x-px flex h-full w-max divide-x divide-border/50',
        className
      )}
    >
      <DailyHeader />
    </div>
  )
}

export type GanttMarkerComponentProps = {
  id: string
  date: Date
  label: ReactNode
  color?: string
  onClick?: () => void
  className?: string
  tooltipContent?: ReactNode
}

export const GanttMarker: FC<GanttMarkerComponentProps> = ({
  date,
  label,
  color,
  onClick,
  className,
  tooltipContent,
}) => {
  const gantt = useContext(GanttContext)
  const left = getOffset(date, gantt.datetimeStart, gantt)
  const pill = (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'pointer-events-auto cursor-pointer whitespace-nowrap rounded-b-md px-2 py-0.5 text-[10px] font-semibold text-white shadow-sm transition-all group-hover:px-2.5 group-hover:py-1 group-hover:text-xs group-hover:shadow-md',
        !onClick && 'cursor-default'
      )}
      style={{ backgroundColor: color ?? 'var(--color-primary)' }}
    >
      {label}
    </button>
  )
  return (
    <div
      className={cn(
        'group pointer-events-none absolute top-0 z-20 flex h-full -translate-x-1/2 select-none flex-col items-center overflow-visible hover:z-[25]',
        className
      )}
      style={{ left }}
    >
      {tooltipContent ? (
        // Auto-height wrapper so the Card hugs the pill — Card's internal
        // `h-full` would otherwise stretch to the full marker column and
        // leave no room for the line below.
        <div className="pointer-events-auto flex-none">
          <Card
            className="border-0 bg-transparent p-0"
            cursorFollow={gantt.cursorFollowTooltips}
            tooltipContent={tooltipContent}
          >
            {pill}
          </Card>
        </div>
      ) : (
        pill
      )}
      <div
        className="w-px grow"
        style={{
          backgroundColor: color ?? 'var(--color-border)',
          opacity: 0.6,
        }}
      />
    </div>
  )
}

export type GanttSidebarItemProps = {
  feature: GanttFeature
  onSelectItem?: (id: string | null) => void
  className?: string
  trailingContent?: ReactNode
}

export const GanttSidebarItem: FC<GanttSidebarItemProps> = ({
  feature,
  onSelectItem,
  className,
  trailingContent,
}) => {
  const gantt = useContext(GanttContext)
  const hideDuration = Boolean(feature.hideDuration)
  const duration = feature.hideDuration
    ? null
    : durationAsSecondsToString(
        (feature.duration ??
          differenceInMinutes(feature.endAt, feature.startAt)) * 60
      )

  const handleClick: MouseEventHandler<HTMLDivElement> = event => {
    if (event.target === event.currentTarget) {
      // Scroll to the feature in the timeline
      gantt.scrollToFeature?.(feature)
      // Call the original onSelectItem callback
      onSelectItem?.(feature.id)
    }
  }

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.key === 'Enter') {
      // Scroll to the feature in the timeline
      gantt.scrollToFeature?.(feature)
      // Call the original onSelectItem callback
      onSelectItem?.(feature.id)
    }
  }

  return (
    <div
      className={cn(
        'relative grid h-full items-center p-2 text-xs hover:bg-secondary sm:p-2.5',
        hideDuration
          ? 'grid-cols-[minmax(0,1fr)]'
          : 'grid-cols-[minmax(0,1fr)_auto] gap-x-3',
        className
      )}
      key={feature.id}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      // biome-ignore lint/a11y/useSemanticElements: "This is a clickable item"
      role="button"
      style={{
        height: 'var(--gantt-row-height)',
      }}
      tabIndex={0}
    >
      {/* <Checkbox onCheckedChange={handleCheck} className="shrink-0" /> */}
      <p className="pointer-events-none min-w-0 truncate text-left font-medium">
        {feature.name}
      </p>
      {!hideDuration && (
        <div className="pointer-events-none shrink-0 text-muted-foreground">
          {trailingContent ?? duration}
        </div>
      )}
    </div>
  )
}

export type GanttSidebarHeaderProps = {
  hideDuration?: boolean
}

export const GanttSidebarHeader: FC<GanttSidebarHeaderProps> = ({
  hideDuration,
}) => (
  <div
    className={cn(
      'sticky top-0 z-10 grid shrink-0 items-end border-border/50 border-b bg-background p-2 font-medium text-muted-foreground text-xs backdrop-blur-sm sm:p-2.5',
      hideDuration
        ? 'grid-cols-[minmax(0,1fr)]'
        : 'grid-cols-[minmax(0,1fr)_auto] gap-x-3'
    )}
    style={{ height: 'var(--gantt-header-height)' }}
  >
    <p className="min-w-0 text-left">Datatype</p>
    {!hideDuration && <p className="shrink-0">Sum</p>}
  </div>
)

export type GanttSidebarGroupProps = {
  children: ReactNode
  name: string
  className?: string
}

export const GanttSidebarGroup: FC<GanttSidebarGroupProps> = ({
  children,
  name,
  className,
}) => (
  <div className={className}>
    <p
      className="w-full truncate p-2.5 text-left font-medium text-muted-foreground text-xs"
      style={{ height: 'var(--gantt-row-height)' }}
    >
      {name}
    </p>
    <div className="divide-y divide-border/50">{children}</div>
  </div>
)

export type GanttSidebarProps = {
  children: ReactNode
  compact?: boolean
  className?: string
  hideDuration?: boolean
}

export const GanttSidebar: FC<GanttSidebarProps> = ({
  children,
  className,
  hideDuration,
}) => (
  <div
    className={cn(
      'sticky left-0 z-30 h-max min-h-full w-56 overflow-clip border-border/50 border-r bg-background/90 backdrop-blur-md sm:w-64',
      className
    )}
    data-roadmap-ui="gantt-sidebar"
  >
    <GanttSidebarHeader hideDuration={hideDuration} />
    <div className="space-y-4">{children}</div>
  </div>
)

export type GanttColumnProps = {
  index: number
  isColumnSecondary?: (item: number) => boolean
}

export const GanttColumn: FC<GanttColumnProps> = ({
  index,
  isColumnSecondary,
}) => {
  return (
    <div
      className={cn(
        'group relative h-full overflow-hidden',
        isColumnSecondary?.(index) ? 'bg-secondary' : ''
      )}
    ></div>
  )
}

export type GanttColumnsProps = {
  columns?: number
  isColumnSecondary?: (item: number) => boolean
  columnWidth?: string
  columnUnits?: number[]
}

export const GanttColumns: FC<GanttColumnsProps> = ({
  columns,
  isColumnSecondary,
  columnWidth = 'var(--gantt-column-width)',
  columnUnits,
}) => {
  const id = useId()
  const renderedColumns = columnUnits?.length ?? columns ?? 0

  return (
    <div
      className="divide grid h-full w-full divide-x divide-border/50"
      style={{
        gridTemplateColumns: columnUnits?.length
          ? columnUnits
              .map(unit => `calc(var(--gantt-column-width) * ${unit})`)
              .join(' ')
          : `repeat(${columns}, ${columnWidth})`,
      }}
    >
      {Array.from({ length: renderedColumns }).map((_, index) => (
        <GanttColumn
          index={index}
          isColumnSecondary={isColumnSecondary}
          key={`${id}-${index}`}
        />
      ))}
    </div>
  )
}

export type GanttFeatureDragHelperProps = {
  featureId: GanttFeature['id']
  direction: 'left' | 'right'
  date: Date | null
}

export const GanttFeatureDragHelper: FC<GanttFeatureDragHelperProps> = ({
  direction,
  featureId,
  date,
}) => {
  const [, setDragging] = useGanttDragging()
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `feature-drag-helper-${featureId}`,
  })

  const isPressed = Boolean(attributes['aria-pressed'])

  useEffect(() => setDragging(isPressed), [isPressed, setDragging])

  return (
    <div
      className={cn(
        'group -translate-y-1/2 !cursor-col-resize absolute top-1/2 z-[3] h-full w-6 rounded-md outline-none',
        direction === 'left' ? '-left-2.5' : '-right-2.5'
      )}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >
      <div
        className={cn(
          '-translate-y-1/2 absolute top-1/2 h-[80%] w-1 rounded-sm bg-muted-foreground opacity-0 transition-all',
          direction === 'left' ? 'left-2.5' : 'right-2.5',
          direction === 'left' ? 'group-hover:left-0' : 'group-hover:right-0',
          isPressed && (direction === 'left' ? 'left-0' : 'right-0'),
          'group-hover:opacity-100',
          isPressed && 'opacity-100'
        )}
      />
      {date && (
        <div
          className={cn(
            '-translate-x-1/2 absolute top-10 hidden whitespace-nowrap rounded-lg border border-border/50 bg-background/90 px-2 py-1 text-foreground text-xs backdrop-blur-lg group-hover:block',
            isPressed && 'block'
          )}
        >
          {format(date, 'MMM dd, yyyy')}
        </div>
      )}
    </div>
  )
}

export type GanttFeatureItemCardProps = Pick<GanttFeature, 'status'> & {
  tooltipContent?: React.ReactNode
  children?: ReactNode
}

export const GanttFeatureItemCard: FC<GanttFeatureItemCardProps> = ({
  status = null,
  tooltipContent,
  children,
}) => {
  const { cursorFollowTooltips } = useContext(GanttContext)
  return (
    <Card
      tooltipContent={tooltipContent}
      cursorFollow={cursorFollowTooltips}
      className={cn(
        'h-full w-full bg-background py-2 text-xs',
        status ? 'hover:border-1 hover:border-primary' : ''
      )}
      style={{ backgroundColor: status ? status.color : 'white' }}
    >
      <div
        className={cn(
          'flex h-full w-full items-center justify-between gap-2 pl-2 text-left'
        )}
      >
        {children}
      </div>
    </Card>
  )
}

export type GanttFeatureItemProps = GanttFeature &
  Omit<ComponentProps<'div'>, 'children' | 'id'> & {
    onMove?: (id: string, startDate: Date, endDate: Date | null) => void
    children?: ReactNode
  }

export const GanttFeatureItem = forwardRef<
  HTMLDivElement,
  GanttFeatureItemProps
>(({ onMove, children, className, ...props }, ref) => {
  const gantt = useContext(GanttContext)
  const {
    id,
    name,
    startAt,
    endAt,
    duration,
    status,
    group,
    tooltipContent,
    manuallyEntered,
    labelClassName,
    ...divProps
  } = props
  const timelineStartDate = useMemo(
    () => gantt.datetimeStart,
    [gantt.datetimeStart]
  )

  // Memoize expensive calculations
  const width = useMemo(
    () => getWidth(startAt, endAt, gantt),
    [startAt, endAt, gantt]
  )
  const offset = useMemo(
    () => getOffset(startAt, timelineStartDate, gantt),
    [startAt, timelineStartDate, gantt]
  )

  return (
    <div
      ref={ref}
      {...divProps}
      className={cn('absolute top-0.5 pointer-events-auto', className)}
      style={{
        ...divProps.style,
        height: 'calc(var(--gantt-row-height) - 4px)',
        width: Math.round(width),
        left: Math.round(offset),
      }}
      data-offset={offset}
    >
      <GanttFeatureItemCard
        status={status ?? group}
        tooltipContent={tooltipContent}
      >
        {children ?? <p className="flex-1 truncate text-xs">{name}</p>}
      </GanttFeatureItemCard>
    </div>
  )
})

GanttFeatureItem.displayName = 'GanttFeatureItem'

export type GanttFeatureListGroupProps = {
  children: ReactNode
  className?: string
}

export const GanttFeatureListGroup: FC<GanttFeatureListGroupProps> = ({
  children,
  className,
}) => (
  <div className={className} style={{ paddingTop: 'var(--gantt-row-height)' }}>
    {children}
  </div>
)

export type GanttFeatureRowProps = {
  features: GanttFeature[]
  handleViewFeature: (feature: GanttFeature) => void
  onFeatureClick?: (feature: GanttFeature) => void
  onFeatureHover?: (feature: GanttFeature | null) => void
  getContextMenuItems?: (
    feature: GanttFeature
  ) => { id: string; label: ReactNode; onClick: () => void }[]
  className?: string
}

export const GanttFeatureRow: FC<GanttFeatureRowProps> = ({
  features,
  handleViewFeature,
  onFeatureClick,
  onFeatureHover,
  getContextMenuItems,
  className,
}) => {
  const translations = useTranslate({
    changeSalaryCalculation: 'gantt.change-salary-calculation',
    manuallyEntered: 'gantt.manually-entered',
  })

  return (
    <div
      className={cn('relative', className)}
      style={{
        height: `0px`,
        minHeight: 'var(--gantt-row-height)',
      }}
    >
      {features.map(feature => {
        const contextMenuItems = getContextMenuItems?.(feature) ?? []
        const hasContextMenu =
          Boolean(feature.status) || contextMenuItems.length > 0

        return (
          <ContextMenu key={feature.id}>
            <ContextMenuTrigger asChild>
              <GanttFeatureItem
                {...feature}
                className={cn(onFeatureClick && 'cursor-pointer')}
                onClick={() => onFeatureClick?.(feature)}
                onMouseEnter={() => onFeatureHover?.(feature)}
                onMouseLeave={() => onFeatureHover?.(null)}
              >
                <p
                  className={cn(
                    'flex-1 truncate text-xs',
                    feature.labelClassName
                  )}
                >
                  {feature.name}
                </p>
                {feature.manuallyEntered && (
                  <span
                    title={translations.manuallyEntered}
                    className="mr-1 shrink-0 rounded-sm bg-white/85 px-1 text-[10px] leading-4 font-bold text-gray-900"
                  >
                    M
                  </span>
                )}
              </GanttFeatureItem>
            </ContextMenuTrigger>
            {hasContextMenu && (
              <ContextMenuContent>
                {feature.status && (
                  <ContextMenuItem
                    className="flex items-center gap-2"
                    onClick={() => handleViewFeature(feature)}
                  >
                    <PencilIcon className="text-foreground" size={16} />
                    {translations.changeSalaryCalculation}
                  </ContextMenuItem>
                )}
                {contextMenuItems.map(item => (
                  <ContextMenuItem
                    key={item.id}
                    className="flex items-center gap-2"
                    onClick={item.onClick}
                  >
                    {item.label}
                  </ContextMenuItem>
                ))}
              </ContextMenuContent>
            )}
          </ContextMenu>
        )
      })}
    </div>
  )
}

export type GanttFeatureListProps = {
  className?: string
  children: ReactNode
}

export const GanttFeatureList: FC<GanttFeatureListProps> = ({
  className,
  children,
}) => (
  <div
    className={cn('absolute top-0 left-0 h-full w-max space-y-4', className)}
    style={{ marginTop: 'var(--gantt-header-height)' }}
  >
    {children}
  </div>
)

export type GanttProviderProps = {
  datetimeStart?: Date
  datetimeEnd?: Date
  range?: Range
  zoom?: number
  columnDurationMinutes?: number
  onAddItem?: (date: Date) => void
  children: ReactNode
  className?: string
  /**
   * Opt-in: use the cursor-following tooltip variant for every Card-backed
   * tooltip inside this Gantt. Default false keeps the Radix Tooltip
   * behavior that salary / timesheets Gantts were built against.
   */
  cursorFollowTooltips?: boolean
}

export const GanttProvider: FC<GanttProviderProps> = ({
  datetimeStart = new Date(),
  datetimeEnd = addDays(new Date(), 1),
  zoom = 100,
  columnDurationMinutes = 60,
  range = 'daily',
  children,
  className,
  cursorFollowTooltips = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const timelineData = useMemo(
    () => createInitialTimelineData(datetimeStart, datetimeEnd),
    [datetimeStart, datetimeEnd]
  )
  const [, setScrollX] = useGanttScrollX()
  const [sidebarWidth, setSidebarWidth] = useState(0)
  const prevZoomRef = useRef(zoom)

  const headerHeight = isMobile ? 72 : 80
  const rowHeight = isMobile ? 60 : 36
  const columnWidth = 5000
  // Memoize CSS variables to prevent unnecessary re-renders
  const cssVariables = useMemo(
    () =>
      ({
        '--gantt-zoom': `${zoom}`,
        '--gantt-column-width': `${(zoom / 2400) * columnWidth}px`,
        '--gantt-header-height': `${headerHeight}px`,
        '--gantt-row-height': `${rowHeight}px`,
        '--gantt-sidebar-width': `${sidebarWidth}px`,
      }) as CSSProperties,
    [zoom, columnWidth, headerHeight, rowHeight, sidebarWidth]
  )

  useEffect(() => {
    const el = scrollRef.current
    if (!el) {
      prevZoomRef.current = zoom
      return
    }
    const prevZoom = prevZoomRef.current
    if (prevZoom !== zoom) {
      const ratio = zoom / prevZoom
      const prevCenter = el.scrollLeft + el.clientWidth / 2
      const newScrollLeft =
        sidebarWidth + (prevCenter - sidebarWidth) * ratio - el.clientWidth / 2
      el.scrollLeft = Math.max(0, newScrollLeft)
      setScrollX(el.scrollLeft)
    }
    prevZoomRef.current = zoom
  }, [zoom, setScrollX, sidebarWidth])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft =
        scrollRef.current.scrollWidth / 2 - scrollRef.current.clientWidth / 2
      setScrollX(scrollRef.current.scrollLeft)
    }
  }, [setScrollX])

  // Update sidebar width when DOM is ready
  useEffect(() => {
    const updateSidebarWidth = () => {
      const sidebarElement = scrollRef.current?.querySelector(
        '[data-roadmap-ui="gantt-sidebar"]'
      ) as HTMLElement | null
      const newWidth = sidebarElement
        ? Math.ceil(sidebarElement.getBoundingClientRect().width)
        : 0
      setSidebarWidth(newWidth)
    }

    // Update immediately
    updateSidebarWidth()

    // Also update on resize or when children change
    const observer = new MutationObserver(updateSidebarWidth)
    if (scrollRef.current) {
      observer.observe(scrollRef.current, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  const scrollToFeature = useCallback(
    (feature: GanttFeature) => {
      const scrollElement = scrollRef.current
      if (!scrollElement) {
        return
      }

      // Calculate timeline start date from timelineData
      const timelineStartDate = timelineData[0] ?? new Date()

      // Calculate the horizontal offset for the feature's start date
      const offset = getOffset(feature.startAt, timelineStartDate, {
        zoom,
        columnWidth,
        columnDurationMinutes,
      })

      // Scroll to align the feature's start with the right side of the sidebar
      const targetScrollLeft = Math.max(0, offset)

      scrollElement.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      })
    },
    [timelineData, zoom, columnWidth, columnDurationMinutes]
  )

  const contextValue = useMemo<GanttContextProps>(
    () => ({
      zoom,
      range,
      headerHeight,
      columnWidth,
      columnDurationMinutes,
      sidebarWidth,
      datetimeStart,
      datetimeEnd,
      rowHeight,
      timelineData,
      placeholderLength: 2,
      ref: scrollRef,
      scrollToFeature,
      cursorFollowTooltips,
    }),
    [
      zoom,
      range,
      headerHeight,
      columnWidth,
      columnDurationMinutes,
      sidebarWidth,
      datetimeStart,
      datetimeEnd,
      rowHeight,
      timelineData,
      scrollToFeature,
      cursorFollowTooltips,
    ]
  )

  return (
    <GanttContext.Provider value={contextValue}>
      <div
        className={cn(
          'gantt relative isolate grid h-full w-full flex-none select-none overflow-auto rounded-sm',
          range,
          className
        )}
        ref={scrollRef}
        style={{
          ...cssVariables,
          gridTemplateColumns: 'var(--gantt-sidebar-width) 1fr',
        }}
      >
        {children}
      </div>
    </GanttContext.Provider>
  )
}

export type GanttTimelineProps = {
  children: ReactNode
  className?: string
  style?: CSSProperties
}

export const GanttTimeline: FC<GanttTimelineProps> = ({
  children,
  className,
  style,
}) => (
  <div
    className={cn(
      'relative flex h-full w-max flex-none overflow-clip bg-white',
      className
    )}
    style={style}
  >
    {children}
  </div>
)
